import { useMutation, useQuery } from '@tanstack/react-query';
import { api, API_BASE_URL, ApiError } from '@/lib/api';
import { getAccessToken } from '@/lib/auth';
import { isProblemDetails } from '@/types/api';

/**
 * Hooks für den wt-client-Download auf der Howto-Seite.
 *
 * - `useClientDownloadInfo` liest Dateiliste + SHA256 (Integritätsprüfung).
 * - `useDownloadClientBundle` lädt das ZIP-Bundle (wt-client.py + README)
 *   und stößt den Browser-Download an. Ein schlichtes <a href> reicht
 *   nicht: der Endpunkt verlangt den Bearer-Token, also fetch -> Blob.
 *
 * Typen lokal definiert, bis der Gate-Agent openapi.yaml/api.gen.ts zieht.
 */

export interface ClientFileInfo {
  filename: string;
  size_bytes: number;
  sha256: string;
}

export interface ClientDownloadInfo {
  bundle_filename: string;
  bundle_sha256: string;
  files: ClientFileInfo[];
}

export const clientDownloadKeys = {
  info: ['client-download', 'info'] as const,
};

export function useClientDownloadInfo(): ReturnType<typeof useQuery<ClientDownloadInfo>> {
  return useQuery<ClientDownloadInfo>({
    queryKey: clientDownloadKeys.info,
    queryFn: () => api.get<ClientDownloadInfo>('/client/download/info'),
  });
}

/** Lädt eine Datei mit Bearer-Header und speichert sie via Blob-Link. */
async function downloadWithAuth(path: string, fallbackName: string): Promise<void> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) {
    let raw: unknown = null;
    try {
      raw = await res.json();
    } catch {
      raw = null;
    }
    throw new ApiError(res.status, isProblemDetails(raw) ? raw : null, raw);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function useDownloadClientBundle(): ReturnType<
  typeof useMutation<void, Error, void>
> {
  return useMutation<void, Error, void>({
    mutationFn: () => downloadWithAuth('/client/download', 'wt-client.zip'),
  });
}
